import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RotateCcw, Target, ArrowLeft, Search } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import OptimizedImage from '@/components/OptimizedImage';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import { ReviewSection } from '@/components/ReviewSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { familieopstellingReviews } from '@/data/reviews';
import familieVisual from '@/assets/webp bestanden/172 - TwentyTwelvePhotography_Systemique.webp';
import familieVisualSecondary from '@/assets/webp bestanden/263 - TwentyTwelvePhotography_Systemique.webp';

const FAMILIEOPSTELLING_FAQS = [
  {
    q: 'Wat is een familieopstelling?',
    a: 'Een familieopstelling is een systemische methode waarbij representanten of symbolen zichtbaar maken hoe jij in je familiesysteem staat. Je krijgt inzicht in de onderstroom: plek, loyaliteit, uitsluiting, oude familiedynamiek.',
  },
  {
    q: 'Waar vinden familieopstellingen plaats?',
    a: 'Opstellingenmiddagen bij Systemique zijn in Uitgeest (Het Atelier) en Amsterdam IJburg (Stadsoase naar Zee). Data en tijden staan in de agenda. 1-op-1 is in de praktijk in Castricum.',
  },
  {
    q: 'Hoe werkt een familieopstelling?',
    a: 'Je werkt met representanten of symbolen in een kleine groep. Je komt als vraaginbrenger met een eigen vraag, of als representant. Familieopstellingen kunnen ook onderdeel zijn van een 1-op-1 traject.',
  },
  {
    q: 'Kan ik een strippenkaart gebruiken voor familieopstellingen?',
    a: 'Ja. Met een strippenkaart doe je mee aan opstellingenmiddagen als representant. Bekijk de agenda voor komende data en koop een strippenkaart via systemique.nl/koop-strippenkaart.',
  },
  {
    q: 'Wat zijn opstellingenmiddagen?',
    a: 'Open middagen voor familie- en organisatieopstellingen in Uitgeest (Het Atelier) en Amsterdam IJburg (Stadsoase naar Zee). Je komt als vraaginbrenger of als representant. Tijden verschillen per datum; zie de agenda. Los ticket of, als representant, een strippenkaart.',
  },
  {
    q: 'Voor wie zijn familieopstellingen geschikt?',
    a: 'Voor wie patronen wil doorbreken, inzicht wil in familie- of relatiepatronen, of worstelt met verlies, rouw of conflict. Ook als je je plek in de familie of in relaties beter wilt begrijpen.',
  },
  {
    q: 'Wat kost een familieopstelling?',
    a: 'Als vraaginbrenger €100 per opstellingenmiddag. Als representant €75 per los ticket, of €130 voor een strippenkaart van 3 strippen (geen vervaldatum). Bekijk de agenda voor komende data.',
  },
];

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

const FamilieOpstelling = () => {
  return (
    <>
      <SEO 
        title="Familieopstellingen Castricum, Amsterdam & Uitgeest | Systemique"
        description="Familieopstellingen in Castricum, Amsterdam en Uitgeest. Doorbreek patronen, krijg inzicht in de onderstroom en zet duurzame stappen met systemisch werk."
        keywords="familieopstellingen Castricum, familieopstellingen Amsterdam, familieopstellingen Uitgeest, familieopstelling Castricum, familieopstelling Amsterdam, familieopstelling Haarlem, systemisch werk Castricum, systemisch werk Amsterdam, familieopstelling Noord-Holland, familieopstelling Zaanstreek, familieopstelling Zaandam, familieopstelling Alkmaar, familieopstelling Hoofddorp, familieopstelling Uitgeest, systemisch werk Beverwijk, familieopstelling Heerhugowaard, opstellingenmiddagen Noord-Holland, familieopstelling strippenkaart"
        canonical="https://systemique.nl/familieopstellingen"
        ogTitle="Familieopstellingen Castricum & Amsterdam | Systemisch Werk | Systemique"
        ogDescription="Opstellingenmiddagen in Uitgeest (Het Atelier) en Amsterdam IJburg (Stadsoase naar Zee). Doorbreek patronen en krijg zicht op de onderstroom. Strippenkaart of los ticket."
      />
      
      {/* Structured Data - Service (voor local SEO met areaServed) */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Familieopstellingen Service - Castricum, Amsterdam en Uitgeest",
        "description": "Opstellingenmiddagen voor familieopstellingen in Uitgeest (Het Atelier) en Amsterdam IJburg (Stadsoase naar Zee). 1-op-1 in de praktijk in Castricum.",
        "provider": {
          "@type": "Organization",
          "name": "Systemique",
          "url": "https://systemique.nl",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Castricum",
            "addressRegion": "Noord-Holland",
            "addressCountry": "NL"
          }
        },
        "areaServed": [
          {"@type": "City", "name": "Castricum"},
          {"@type": "City", "name": "Amsterdam"},
          {"@type": "City", "name": "Haarlem"},
          {"@type": "City", "name": "Zaandam"},
          {"@type": "City", "name": "Hoofddorp"},
          {"@type": "City", "name": "Amstelveen"},
          {"@type": "City", "name": "Hilversum"},
          {"@type": "City", "name": "Alkmaar"},
          {"@type": "City", "name": "Beverwijk"},
          {"@type": "City", "name": "Heemskerk"},
          {"@type": "City", "name": "Uitgeest"},
          {"@type": "City", "name": "Heerhugowaard"},
          {"@type": "City", "name": "Hoorn"},
          {"@type": "City", "name": "Purmerend"},
          {"@type": "AdministrativeArea", "name": "Zaanstreek"},
          {"@type": "State", "name": "Noord-Holland"}
        ],
        "serviceType": "Familieopstelling",
        "offers": {
          "@type": "Offer",
          "description": "Opstellingenmiddagen in Uitgeest (Het Atelier) en Amsterdam IJburg (Stadsoase naar Zee)"
        }
      })}
      </script>
      
      {/* Structured Data - BreadcrumbList */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://systemique.nl/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Familieopstellingen Castricum en Amsterdam",
            "item": "https://systemique.nl/familieopstellingen"
          }
        ]
      })}
      </script>
      
      {/* FAQPage Schema */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
            mainEntity: FAMILIEOPSTELLING_FAQS.map(({ q, a }) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: {
                "@type": "Answer",
                text: a,
              },
            })),
      })}
      </script>

      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" aria-hidden="true" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Familie<span className="italic font-light font-serif text-[#F9EBEF]">opstellingen</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Systemisch werk voor persoonlijke ontwikkeling en inzicht in familiepatronen
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="font-serif text-2xl md:text-4xl text-[#854C73] italic font-light leading-snug">
            Voel je dat oude patronen zich blijven herhalen?
          </p>
          <p className="mt-5 text-lg md:text-xl text-[#6b4912] font-light leading-relaxed">
            Loop je vast in relaties, gezin of werk? Dan is dit een manier om zacht en helder te zien wat eronder zit.
          </p>
          <div className="h-px w-16 bg-[#854C73]/25 mx-auto my-10 md:my-12" />
          <p className="text-lg md:text-xl text-[#6b4912] leading-relaxed font-light">
            Een familieopstelling is een systemische methode waarbij representanten of symbolen zichtbaar maken hoe jij in je familiesysteem staat.
          </p>
          <p className="mt-6 text-base md:text-lg text-[#6b4912]/75 leading-relaxed font-light">
            Opstellingenmiddagen bij Systemique zijn in Uitgeest (Het Atelier) en Amsterdam IJburg (Stadsoase naar Zee). Je komt als vraaginbrenger of als representant. In één middag wordt de onderstroom zichtbaar: plek, loyaliteit, uitsluiting, oude familiedynamiek.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">

          {/* Editorial split: foto links, twee cards midden, extra foto rechtsonder */}
          <div className="grid md:grid-cols-12 gap-6 md:gap-8 mb-24 items-stretch max-w-5xl mx-auto">

            {/* Linker kolom: grote staande foto */}
            <div className="md:col-span-4 order-2 md:order-1">
              <div className="relative md:sticky md:top-28">
                <div className="group overflow-hidden rounded-[2rem] shadow-xl">
                  <div className="aspect-[4/5] overflow-hidden rounded-[2rem]">
                    <OptimizedImage
                      src={familieVisual}
                      webpSrc={familieVisual}
                      alt="Warme en veilige setting tijdens familieopstelling bij Systemique"
                      className="w-full h-full object-cover object-[50%_40%] transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      width={900}
                      height={1125}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Middelste kolom: twee cards even hoog, gestapeld */}
            <div className="md:col-span-5 order-1 md:order-2 grid grid-rows-2 gap-6 md:gap-8">
              <SpotlightCard className="p-6 md:p-8">
                <div className="p-2.5 rounded-xl bg-[#FAEBEE] text-[#854C73] mb-4 w-fit">
                  <RotateCcw className="w-6 h-6" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-[#854C73] mb-4 font-bold">
                  Wanneer je voelt dat iets niet klopt
                </h3>
                <ul className="space-y-2.5 text-[#6b4912] leading-relaxed flex-grow text-[15px]">
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Er is spanning of conflict in de familie of in een relatie</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Je voelt je onzeker, ondergewaardeerd of gevangen in een rol</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Patronen van uitstel, ziekte, verdriet of conflict herhalen zich</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Je worstelt met verlies of rouw en weet niet goed hoe verder</span>
                  </li>
                </ul>
              </SpotlightCard>

              <SpotlightCard className="p-6 md:p-8">
                <div className="p-2.5 rounded-xl bg-[#FAEBEE] text-[#854C73] mb-4 w-fit">
                  <Search className="w-6 h-6" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-[#854C73] mb-4 font-bold">
                  Wanneer je iets wilt ontwikkelen
                </h3>
                <ul className="space-y-2.5 text-[#6b4912] leading-relaxed flex-grow text-[15px]">
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Je wilt je plek in de familie of in relaties beter begrijpen</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Je wilt inzicht in belangrijke keuzes voor je persoonlijke of professionele leven</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Je wilt harmonie en verbinding creëren in relaties</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#854C73] text-base mt-0.5">•</span>
                    <span>Je wilt oude patronen doorbreken en ruimte maken voor groei</span>
                  </li>
                </ul>
              </SpotlightCard>
            </div>

            {/* Rechter kolom: nieuwe foto (vierkant), uitgelijnd met onderkant card 2 */}
            <div className="md:col-span-3 order-3 md:order-3 flex md:justify-end md:items-end">
              <div className="group w-full overflow-hidden rounded-[2rem] shadow-xl">
                <div className="aspect-square overflow-hidden rounded-[2rem]">
                  <OptimizedImage
                    src={familieVisualSecondary}
                    webpSrc={familieVisualSecondary}
                    alt="Sfeerbeeld familieopstelling — systemisch werk bij Systemique"
                    className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    width={800}
                    height={800}
                  />
                </div>
              </div>
            </div>

          </div>

          {/* Closing Section - Maatwerk of Opstellingsdagen */}
          <div className="bg-[#854C73] rounded-[2.5rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 pointer-events-none">
              <GrainTexture opacity={0.2} />
              <GeoPattern />
              <div className="absolute top-0 left-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-[#FAEBEE]/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-[#FAEBEE]/10 rounded-full translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative z-10 max-w-3xl mx-auto">
              <Target className="w-16 h-16 text-[#FAEBEE] mx-auto mb-8 opacity-90" strokeWidth={1} aria-hidden="true" />
              <h3 className="font-serif text-3xl md:text-5xl text-[#FAEBEE] mb-6">
                Maatwerk of <span className="italic font-light font-serif">Opstellingsdagen</span>
              </h3>
              <p className="text-[#FAEBEE]/80 text-lg md:text-xl leading-relaxed mb-10 font-light">
                Een familieopstelling kan onderdeel zijn van een langer coachtraject. Samen kijken we wat het beste past. De opstellingen vinden plaats in een veilige en respectvolle setting. <strong className="text-[#FAEBEE]">Wil je dit zelf ervaren?</strong> Bekijk de volgende data in mijn agenda.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button variant="white" size="lg" asChild className="px-10 py-6 text-lg shadow-xl hover:scale-105 transition-transform cursor-pointer">
                  <Link to="/1-op-1">1-op-1</Link>
                </Button>
                <Button variant="white" size="lg" asChild className="px-10 py-6 text-lg shadow-xl hover:scale-105 transition-transform cursor-pointer">
                  <Link to="/agenda">Naar opstellingsdagen</Link>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="py-24 px-6 bg-white relative">
        <GrainTexture opacity={0.08} />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-10 text-center">
            Veelgestelde <span className="italic font-light">vragen</span>
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {FAMILIEOPSTELLING_FAQS.map((faq, i) => (
              <AccordionItem
                key={faq.q}
                value={`item-${i + 1}`}
                className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors"
              >
                <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Review Sectie */}
      <ReviewSection reviews={familieopstellingReviews} />
    </>
  );
};

export default FamilieOpstelling;
